/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Volume2, VolumeX, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
// Patterns: arithmetic, doubling, decreasing
const generatePattern = () => {
  const types = ["add", "double", "subtract", "alternate"];
  const t = types[Math.floor(Math.random() * types.length)];
  let seq = [];
  let answer = 0;
  if (t === "add") {
    const start = Math.floor(Math.random() * 5) + 1;
    const step = Math.floor(Math.random() * 4) + 1;
    seq = [start, start + step, start + step * 2, start + step * 3];
    answer = start + step * 4;
  } else if (t === "double") {
    const start = Math.floor(Math.random() * 3) + 1;
    seq = [start, start * 2, start * 4, start * 8];
    answer = start * 16;
  } else if (t === "subtract") {
    const start = Math.floor(Math.random() * 5) + 15;
    const step = Math.floor(Math.random() * 3) + 1;
    seq = [start, start - step, start - step * 2, start - step * 3];
    answer = start - step * 4;
  } else { // alternate
    const a = Math.floor(Math.random() * 5) + 1;
    const b = a + Math.floor(Math.random() * 4) + 2;
    seq = [a, b, a, b];
    answer = a;
  }
  return { seq, answer };
};

const speakText = (t) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const TebakPola = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [pattern, setPattern] = useState(null);
  const [opts, setOpts] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [won, setWon] = useState(false);
  const totalRounds = 12;

  const newRound = useCallback(() => {
    const p = generatePattern();
    const wrongs = new Set();
    while (wrongs.size < 3) {
      const w = p.answer + (Math.floor(Math.random() * 9) - 4);
      if (w !== p.answer && w >= 0 && w !== Number(p.answer)) wrongs.add(w);
    }
    const all = [...wrongs, p.answer].sort(() => Math.random() - 0.5);
    setPattern(p); setOpts(all); setFeedback(null);
    setTimeout(() => { if (soundOn) speakText(`Continue the pattern: ${p.seq.join(", ")}`); }, 300);
  }, [soundOn]);

  useEffect(() => { newRound(); /* eslint-disable-next-line */ }, []);

  const handlePick = (n) => {
    if (feedback) return;
    if (n === pattern.answer) {
      setFeedback({ type: "ok", text: `Benar! ${n}!` });
      setScore(s => s + 1);
      if (soundOn) speakText(`Correct! The pattern continues to ${n}`);
    } else {
      setFeedback({ type: "wrong", text: `Itu ${n}` });
      if (soundOn) speakText(`The correct answer is ${pattern.answer}`);
    }
    setTimeout(() => {
      if (round + 1 >= totalRounds) { setWon(true); recordGameWon("pola"); }
      else { setRound(r => r + 1); newRound(); }
    }, 1500);
  };

  if (won) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="pola-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-6xl block mb-3">🔢</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Selesai!</h2>
          <p className="font-fredoka text-gold-light/80 mb-4">Skor: {score}/{totalRounds}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setScore(0); setRound(0); setWon(false); newRound(); }}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-3 pt-3" data-testid="pola-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">🔢 {round + 1}/{totalRounds} • Skor: {score}</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <motion.div key={round} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="glass-card-strong rounded-2xl p-6 w-full text-center mb-3">
        <p className="font-fredoka text-gold-light/70 text-sm mb-3">Lanjutkan polanya:</p>
        <div className="flex justify-center gap-3 flex-wrap">
          {pattern?.seq.map((n, i) => (
            <span key={i} className="font-fredoka text-4xl sm:text-5xl text-gold font-black w-14 sm:w-16 text-center">{n}</span>
          ))}
          <span className="font-fredoka text-4xl sm:text-5xl text-pink-400 font-black w-14 sm:w-16 text-center animate-pulse">?</span>
        </div>
      </motion.div>

      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }} className="mb-3" data-testid={`pola-feedback-${feedback.type}`}>
            <span className={`font-fredoka font-bold text-base px-4 py-2 rounded-full ${feedback.type === "ok" ? "bg-green-500 text-white" : "bg-red-400 text-white"}`}>
              {feedback.type === "ok" ? "✅" : "❌"} {feedback.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-4 w-full max-w-2xl">
        {opts.map((n, i) => (
          <motion.button key={`${n}-${i}`} data-testid={`pola-opt-${i}`}
            onClick={() => handlePick(n)} disabled={!!feedback}
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }}
            className="ingredient-3d quiz-option-btn-large text-center text-slate-800 font-fredoka font-bold">
            {n}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default TebakPola;
